import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/store-hooks";
import {
  BellCrossed,
  CaretUp,
  More,
  NodePencil,
  Search,
  Siren,
} from "../../../icons";
import Button from "../../common/button";
import ProfileImageComp from "../../common/profile";
import { useChatContext } from "stream-chat-react";
import {
  getAllChattedChannel,
  getAllTeamChannelWithoutJoin,
} from "../../../store/actions/chatActions";
import { useDebounce } from "../../../hooks/debounce-hooks";
import GroupChat from "../group-chat";
import { conversionOfPostDate } from "../../../helpers";
import { ChannelInfoIcon } from "../../../icons/group-chat-icons";
import { ROUTES } from "../../../constants/routes";
import { useNavigate } from "react-router-dom";
import BecomeMemberButton from "../../common/membership/BecomeMemberButton";
import ProfileBadgeAvatar from "../../user/user-profile/profile-avatar";
import { Channel } from "stream-chat";
import { MixpanelEvent, trackEvent } from "../../../analytics/mixpanel";

// channel types
type ChannelType = "messaging" | "team" | "adminOnly";

const RightPanel = ({
  setIsShowRightPanel = () => {},
  isShowRightPanel,
  handleWhenSelectDataAndOpenLeftPanel,
  setSelectedUserData = () => {},
  setIsShowLeftPanel = () => {},
  setIsShowLeftPanelfull = () => {},
  handleLeftPanelWithList = () => {},
}: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user: currentUserDetails } = useAppSelector(
    (state) => state.home?.homeData || {},
  );
  const { isOpcMember } = useAppSelector((state: any) => state.home);

  const allChattedChannel = useAppSelector(
    (state) => state.chat.allChattedChannel,
  );

  const { _id: userChatId } = currentUserDetails || {};

  const { client: chatClient } = useChatContext();

  const [searchText, setSearchText] = useState("");
  const [channelType, setChannelType] = useState<ChannelType>("messaging");
  const [unreadCount, setunreadCount] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({
    messaging: 0,
    team: 0,
    adminOnly: 0,
  });
  const debouncedSearch = useDebounce(searchText, 500);

  useEffect(() => {
    dispatch(getAllTeamChannelWithoutJoin({ chatClient, userChatId }));
    dispatch(
      getAllChattedChannel({ chatClient, userChatId, type: channelType }),
    );
    chatClient
      ?.getUnreadCount()
      .then((res) => {
        setunreadCount(res.total_unread_count || 0);
        const channelTypeUnreadCounts = res.channel_type.reduce(
          (acc, type) => {
            acc[type.channel_type as ChannelType] = type.unread_count;
            return acc;
          },
          { messaging: 0, team: 0, adminOnly: 0 },
        );
        setUnreadCounts(channelTypeUnreadCounts);
      })
      .catch(() => {
        setunreadCount((prev) => prev);
      });

    chatClient?.on("message.new", (event) => {
      setunreadCount(event.total_unread_count || 0);
    });

    chatClient?.on("message.read", () => {
      chatClient
        .getUnreadCount()
        .then((res) => {
          setunreadCount(res.total_unread_count || 0);
          const channelTypeUnreadCounts = res.channel_type.reduce(
            (acc, type) => {
              acc[type.channel_type as ChannelType] = type.unread_count;
              return acc;
            },
            { messaging: 0, team: 0, adminOnly: 0 },
          );
          setUnreadCounts(channelTypeUnreadCounts);
        })
        .catch(() => {
          setunreadCount((prev) => prev);
        });
    });
  }, []);

  useEffect(() => {
    if (chatClient) {
      dispatch(
        getAllChattedChannel({
          chatClient: chatClient,
          userChatId: userChatId,
          search: debouncedSearch,
          type: channelType,
        }),
      );
    }
  }, [chatClient, debouncedSearch, channelType]);

  const renderNonmemberState = () => {
    return (
      <div className="p-3 mt-28">
        <div className="mb-4 text-center">
          <h2 className="text-2xl font-semibold">
            Become a member to start a chat
          </h2>
          <p className="text-gray-500">Only members can chat.</p>
          <div className="flex justify-center mt-4 space-x-4">
            <img
              src="/assets/logo/empty-chat.png"
              className="w-48"
              alt="placeholder"
            />
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-center mx-4">
          <BecomeMemberButton buttonCustomClass="py-3 px-4 bg-secondary-300 text-white rounded-[10px]" />
        </div>
      </div>
    );
  };

  const renderMemberState = () => {
    return (
      <div className="pt-20  chat-list-container-right">
        <div className="w-full p-3">
          <div className="w-full px-2 py-2 bg-neutral-100 rounded-md justify-start items-center gap-1 inline-flex">
            <Search width={12} height={12} fillColor="#808080" />
            <input
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              className="grow shrink basis-0 placeholder:text-primary-550 bg-neutral-100 text-sm font-normal leading-3 focus:outline-none"
              placeholder={`Search ${channelType === "messaging" ? "chats" : "groups"}`}
            ></input>
          </div>
        </div>
        <div className="flex gap-2 px-4 pb-3">
          <button
            type="button"
            className={`relative px-3 py-2 ${channelType === "messaging" ? "bg-[#007aff] text-white" : "text-neutral-700 border border-[#e1e1e1]"}  rounded-3xl text-xs font-semibold leading-3`}
            onClick={() => {
              setChannelType("messaging");
            }}
          >
            Chat
            {unreadCounts.messaging > 0 && (
              <div className="absolute -top-[2px] right-1 w-[8px] h-[8px] bg-[#ED6664] rounded-full "></div>
            )}
          </button>
          <button
            type="button"
            className={`relative px-3 py-2 rounded-3xl text-xs font-semibold leading-3 ${channelType === "team" || channelType === "adminOnly" ? "bg-[#007aff] text-white" : "text-neutral-700 border border-[#e1e1e1]"}`}
            onClick={() => {
              setChannelType("team");
            }}
          >
            Groups
            {(unreadCounts.team > 0 || unreadCounts.adminOnly > 0) && (
              <div className="absolute -top-1 right-1 w-[8px] h-[8px] bg-[#ED6664] rounded-full "></div>
            )}
          </button>
        </div>
        {channelType === "team" || channelType === "adminOnly" ? (
          renderGroupPage()
        ) : (
          <ChannelList />
        )}
      </div>
    );
  };

  const renderEmptyState = (type?: string) => {
    return (
      <div className="flex flex-col gap-2">
        {/* No New Chats Section */}
        <div className="flex flex-col justify-center items-center">
          <h2 className="text-lg font-semibold">
            {type === "messaging" ? "No New Chats" : "No Groups Joined Yet"}
          </h2>
          <p className="text-gray-500 text-xs">
            {type === "messaging"
              ? "Start one with members"
              : "Join one to get information"}
          </p>
          <div className="flex justify-center">
            <img
              src="/assets/logo/empty-chat.png"
              className="w-48"
              alt="placeholder"
            />
          </div>
        </div>

        {/* Button */}
        {type === "messaging" && (
          <div className="flex justify-center mx-4">
            <Button
              customClasses="py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-300"
              onClick={() => {
                setIsShowLeftPanelfull(true);
                setIsShowLeftPanel(true);
              }}
            >
              Start a chat
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderGroupPage = () => {
    return (
      <>
        <GroupChat
          chatClient={chatClient}
          handleWhenSelectDataAndOpenLeftPanel={
            handleWhenSelectDataAndOpenLeftPanel
          }
        />
        {allChattedChannel.length === 0
          ? searchText.length > 0
            ? renderNoMatchFound()
            : renderEmptyState("team")
          : null}
      </>
    );
  };

  const ChannelList = () => {
    const [moreOpen, setMoreOpen] = useState(false);
    const [lastMsgs, setlastMsgs] = useState<any[]>([]);
    useEffect(() => {
      setlastMsgs(
        allChattedChannel.map((channel: Channel) => {
          return channel?.state?.messageSets?.[0]?.messages[
            channel.state.messages.length - 1
          ];
        }),
      );
    }, []);

    return (
      <div className="pb-8 overflow-y-scroll h-full divide-y divide-gray-200 flex flex-col">
        {allChattedChannel.length === 0 ? (
          searchText.length > 0 ? (
            renderNoMatchFound()
          ) : (
            renderEmptyState("messaging")
          )
        ) : (
          <>
            {allChattedChannel.map((item: Channel, index: number) => {
              if (item.type !== "messaging") return null; // Only show channels of type "messaging"
              const allKeys = Object.keys(item?.state?.members);
              const otherUser: any = allKeys?.find(
                (member: any) => member !== userChatId,
              );
              item.on("message.new", (event) => {
                setlastMsgs((prev) => {
                  const newLastMsgs = [...prev];
                  newLastMsgs[index] = event.message;
                  return newLastMsgs;
                });
              });
              const details = item?.state?.members[otherUser]?.user;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 py-3  hover:bg-gray-100 group"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="relative w-10">
                      <ProfileImageComp
                        imageUrl={details?.image as string}
                        customClass="w-10 h-10 rounded-full"
                        is_opc={false}
                      />
                      {details?.online ? (
                        <div className="w-2 h-2 rounded-full bg-[#01754f] absolute right-1 bottom-0"></div>
                      ) : null}
                    </div>
                    <div
                      className="ml-3 flex items-start flex-col cursor-pointer flex-grow"
                      onClick={() => {
                        setIsShowLeftPanel(true);
                        setSelectedUserData({
                          user: details,
                          type: channelType,
                        });
                        setIsShowLeftPanelfull(true);
                        setIsShowRightPanel(false);
                      }}
                    >
                      <h4 className=" text-sm font-semibold text-gray-800">
                        {details?.name}
                      </h4>
                      <p className="text-xs text-neutral-700 truncate max-w-52">
                        {(lastMsgs[index] &&
                          (lastMsgs[index]?.user?.id === userChatId
                            ? "You: "
                            : "") + lastMsgs[index]?.text) ||
                          ""}
                      </p>
                    </div>
                    <div className="text-xs text-neutral-700 group-hover:hidden flex flex-col justify-between items-end gap-1">
                      {item?.state?.messageSets?.[0]?.messages[
                        item?.state?.messageSets?.[0]?.messages.length - 1
                      ]?.created_at &&
                        conversionOfPostDate(
                          item?.state?.messageSets?.[0]?.messages[
                            item?.state?.messageSets?.[0]?.messages.length - 1
                          ]?.created_at,
                        )}
                      <div>
                        {item?.state?.unreadCount > 0 && (
                          <div className="w-4 h-4 rounded-full text-xs text-white bg-[#FF271B] flex justify-center items-center ">
                            {item.state.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="relative hidden hover:cursor-pointer group-hover:block"
                      onMouseEnter={() => {
                        setMoreOpen(true);
                      }}
                      onMouseLeave={() => {
                        setMoreOpen(false);
                      }}
                    >
                      <More />
                      {moreOpen && (
                        <div className="absolute top-4 right-0 bg-white divide-y w-max rounded-xl shadow backdrop-blur-[166.01px] flex-col justify-start items-start inline-flex">
                          <div
                            className="flex gap-2 justify-start text-sm w-full p-3"
                            onClick={() => {
                              navigate(
                                ROUTES.USER_PROFILE.replace(
                                  ":user_id",
                                  details?.id as string,
                                ),
                              );
                            }}
                          >
                            <ChannelInfoIcon /> <p>Profile info</p>
                          </div>
                          <div
                            className="flex gap-2 justify-start text-sm w-full p-3"
                            onClick={() => {
                              if (item?.state?.mutedUsers?.length > 0)
                                item?.mute();
                              else item?.unmute();
                            }}
                          >
                            <BellCrossed />{" "}
                            <p>{`${item?.state?.mutedUsers?.length === 0 ? "Mute" : "Unmute"} notifications`}</p>
                          </div>
                          <div className="flex gap-2 justify-start text-sm w-full p-3">
                            <Siren strokeColor="#ff261a" />{" "}
                            <p className="text-[#ff261a]">Report</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const renderNoMatchFound = () => {
    return (
      <div className="flex flex-col justify-center items-center ">
        <img
          src="/assets/noevents.png"
          alt="No events"
          className="h-[95px] w-[95px]"
        />
        <div className="text-center text-[#090909] text-2xl font-bold font-['SF Pro'] leading-8 tracking-wide">
          Nothing found
        </div>
        <div className="text-center text-neutral-700 text-md font-normal font-['SF Pro'] leading-snug">
          We couldn't find any matches for "{searchText}"
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 border-b border-b-primary-600 cursor-pointer absolute z-[11] w-full top-0 bg-white">
        <div
          className="flex items-center w-full p-4 pr-0"
          onClick={() => {
            if (!isShowRightPanel) {
              trackEvent(MixpanelEvent.BUTTON_PRESSED, {
                button_name: "home_chat_icon_press",
              });
            }
            setIsShowRightPanel(!isShowRightPanel);
          }}
        >
          {currentUserDetails?.badge ? (
            <div className={`relative w-9 h-[36px] `}>
              <ProfileBadgeAvatar
                profileImage={currentUserDetails?.profile_picture}
                customDivClassName="absolute h-9 w-9 border-4 z-1"
                customImgClassName="h-[36px] w-[36px] absolute rounded-full p-1"
                customIconDivClassName="absolute z-[120] -bottom-[3px] left-[14px]"
                customStyle1="w-[50px] h-4  absolute -bottom-2 z-[100] overflow-hidden"
                customStyle2="w-[200px] h-3 absolute top-3 z-10 -left-10 overflow-hidden"
                customStarWidth={9}
                customStarHeight={9}
                customStyle3="w-[28px] h-[28px]"
                badge={currentUserDetails?.badge}
              />
            </div>
          ) : (
            <ProfileImageComp
              imageUrl={currentUserDetails?.profile_picture}
              customClass="h-12 w-12"
              is_opc={currentUserDetails?.memberships?.is_opc}
            />
          )}
          <div className="ml-2 font-semibold text-fs-17 text-primary-900 flex items-center gap-1">
            Messaging
            {unreadCount > 0 && (
              <div className="w-5 h-5 rounded-full text-sm text-white bg-[#FF271B] flex justify-center items-center ">
                {unreadCount}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center p-4 pl-0 space-x-2">
          {isOpcMember ? (
            <div onClick={handleLeftPanelWithList}>
              <NodePencil />
            </div>
          ) : null}
          <div
            className={`ml-2 ${isShowRightPanel ? "rotate-180" : ""}`}
            onClick={() => {
              if (!isShowRightPanel) {
                trackEvent(MixpanelEvent.BUTTON_PRESSED, {
                  button_name: "home_chat_icon_press",
                });
              }
              setIsShowRightPanel(!isShowRightPanel);
            }}
          >
            <CaretUp />
          </div>
        </div>
      </div>
      {isOpcMember ? renderMemberState() : renderNonmemberState()}
    </>
  );
};

export default RightPanel;
